import type {
  ShapeElementModel,
  TextElementModel,
} from '@blocksuite/affine-model';
import {
  scheduleCanvasArrivals,
  subscribeStreamLainMotionPolicy,
} from '@blocksuite/affine-shared/utils';
import {
  Bound,
  isSerializedXYWH,
  type SerializedXYWH,
} from '@blocksuite/global/gfx';
import {
  type GfxModel,
  GfxPrimitiveElementModel,
  type TextSpan,
} from '@blocksuite/std/gfx';
import { seededRandom } from '@blocksuite/std/inline';
import type { AgentPlayback } from '@blocksuite/store';
import type { Observable } from 'rxjs';

import { agentPointerDurationMs } from '../agent-pointer-motion';

export const AGENT_ARRIVAL_GAP_MS = 250;
export const AGENT_GHOST_MS = 600;

interface CanvasGhost {
  id: string;
  xywh: SerializedXYWH;
}

interface RevealInput {
  playback: Observable<AgentPlayback>;
  view: Window;
  model(id: string): GfxModel | null;
  place(model: GfxModel, xywh: SerializedXYWH | undefined): void;
  zoom(): number;
  ghosts(changed: readonly CanvasGhost[]): void;
  reduced(): boolean;
}

/** Client-local presentation of an agent update; persisted geometry is only read. */
export class AgentCanvasReveal {
  private readonly _moves = new Map<string, (clear: boolean) => void>();
  private readonly _texts = new Map<string, () => void>();
  private readonly _ghosts = new Map<string, CanvasGhost & { timer: number }>();
  private readonly _cleanup: (() => void)[] = [];
  private _disposed = false;

  constructor(private readonly input: RevealInput) {
    const subscription = input.playback.subscribe(playback =>
      this._play(playback)
    );
    this._cleanup.push(() => subscription.unsubscribe());
    const settle = () => {
      if (input.reduced()) this._finish();
    };
    const preference = input.view.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );
    preference.addEventListener('change', settle);
    this._cleanup.push(() => preference.removeEventListener('change', settle));
    this._cleanup.push(subscribeStreamLainMotionPolicy(settle));
  }

  /** A drag preview takes ownership, including when its first bounds equal ours. */
  interrupt(id: string): void {
    this._moves.get(id)?.(false);
  }

  private _publishGhosts(): void {
    this.input.ghosts(
      [...this._ghosts.values()].map(({ id, xywh }) => ({ id, xywh }))
    );
  }

  private _play(playback: AgentPlayback): void {
    if (this._disposed) return;
    if (this.input.reduced()) {
      this._finish();
      return;
    }
    const { input } = this;
    const creates = playback.steps
      .filter(step => step.kind === 'create')
      .map(step => step.target.id);
    scheduleCanvasArrivals(
      creates,
      AGENT_ARRIVAL_GAP_MS,
      input.view.performance.now()
    );
    for (const step of playback.steps) {
      const id = step.target.id;
      this._moves.get(id)?.(true);
      this._texts.get(id)?.();
      const oldGhost = this._ghosts.get(id);
      if (oldGhost) {
        input.view.clearTimeout(oldGhost.timer);
        this._ghosts.delete(id);
        this._publishGhosts();
      }
      if (step.kind === 'edit' && step.textInserted !== undefined) {
        const model = input.model(id);
        if (
          model instanceof GfxPrimitiveElementModel &&
          (model.type === 'shape' || model.type === 'text')
        )
          this._revealText(
            model as ShapeElementModel | TextElementModel,
            step.textInserted,
            [playback.runId, playback.stepId].join(':')
          );
      }
      if (!step.before || !isSerializedXYWH(step.before)) continue;
      if (step.kind === 'move') {
        const model = input.model(id);
        if (model && model.transientXYWH === undefined)
          this._move(model, step.before);
      } else if (step.kind === 'delete') {
        const timer = input.view.setTimeout(() => {
          this._ghosts.delete(id);
          this._publishGhosts();
        }, AGENT_GHOST_MS);
        this._ghosts.set(id, { id, xywh: step.before, timer });
        this._publishGhosts();
      }
    }
  }

  private _move(model: GfxModel, before: SerializedXYWH): void {
    const { input } = this;
    const from = Bound.deserialize(before);
    const destination = model.xywh;
    const to = Bound.deserialize(destination);
    const duration = agentPointerDurationMs(
      Math.hypot(to.x - from.x, to.y - from.y) * input.zoom()
    );
    const start = input.view.performance.now();
    let frame: number | undefined;
    let last = before;
    let stopped = false;
    const stop = (clear: boolean) => {
      if (stopped) return;
      stopped = true;
      if (frame !== undefined) input.view.cancelAnimationFrame(frame);
      this._moves.delete(model.id);
      if (
        clear &&
        input.model(model.id) === model &&
        model.transientXYWH === last
      )
        input.place(model, undefined);
    };
    this._moves.set(model.id, stop);
    input.place(model, before);
    const tick = (now: number) => {
      if (stopped) return;
      // Never clear or overwrite bounds installed by somebody else's preview.
      if (input.model(model.id) !== model || model.transientXYWH !== last) {
        stop(false);
        return;
      }
      if (input.reduced() || model.xywh !== destination) {
        stop(true);
        return;
      }
      const progress = Math.min(1, Math.max(0, (now - start) / duration));
      if (progress === 1) {
        stop(true);
        return;
      }
      const eased =
        progress < 0.5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2;
      last = new Bound(
        from.x + (to.x - from.x) * eased,
        from.y + (to.y - from.y) * eased,
        from.w + (to.w - from.w) * eased,
        from.h + (to.h - from.h) * eased
      ).serialize();
      input.place(model, last);
      if (!stopped) frame = input.view.requestAnimationFrame(tick);
    };
    frame = input.view.requestAnimationFrame(tick);
  }

  /** Types the inserted span; the text before and after it stays shown. */
  private _revealText(
    model: ShapeElementModel | TextElementModel,
    inserted: TextSpan,
    seed: string
  ): void {
    const text = model.text;
    if (!text || model.transientTextHidden !== undefined) return;
    const to = Math.max(0, Math.min(text.length, inserted.to));
    const from = Math.max(0, Math.min(to, inserted.from));
    const count = to - from;
    if (!count) return;
    const { input } = this;
    const random = seededRandom(seed);
    const arrivals: number[] = [];
    let duration = 0;
    for (let index = 0; index < count; index++) {
      duration += 30 + random() * 40;
      arrivals.push(duration);
    }
    const scale = Math.min(1, 2_000 / duration);
    const start = input.view.performance.now();
    let shown = 0;
    let last: TextSpan = { from, to };
    let stopped = false;
    let frame: number | undefined;
    const stop = () => {
      if (stopped) return;
      stopped = true;
      if (frame !== undefined) input.view.cancelAnimationFrame(frame);
      text.unobserve(stop);
      this._texts.delete(model.id);
      if (input.model(model.id) === model && model.transientTextHidden === last)
        model.transientTextHidden = undefined;
    };
    this._texts.set(model.id, stop);
    text.observe(stop);
    model.transientTextHidden = last;
    const tick = (now: number) => {
      if (stopped) return;
      if (
        input.reduced() ||
        input.model(model.id) !== model ||
        model.transientTextHidden !== last
      ) {
        stop();
        return;
      }
      while (shown < count && arrivals[shown] * scale <= now - start) shown++;
      if (shown === count || now - start >= 2_000) {
        stop();
        return;
      }
      if (last.from !== from + shown) {
        last = { from: from + shown, to };
        model.transientTextHidden = last;
      }
      frame = input.view.requestAnimationFrame(tick);
    };
    frame = input.view.requestAnimationFrame(tick);
  }

  private _finish(): void {
    for (const stop of this._moves.values()) stop(true);
    for (const stop of this._texts.values()) stop();
    for (const ghost of this._ghosts.values())
      this.input.view.clearTimeout(ghost.timer);
    if (this._ghosts.size) {
      this._ghosts.clear();
      this._publishGhosts();
    }
  }

  dispose(): void {
    if (this._disposed) return;
    this._disposed = true;
    this._cleanup.forEach(cleanup => cleanup());
    this._finish();
  }
}
